import axios from 'axios';
import { API_URL } from '../config/api';
import { clearStoredAuthSession, isAuthSessionRejected } from '../utils/authSessionSync';

// Create axios instance with default config
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add token to requests if available
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle response errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status;
    const data = error.response?.data;
    // Backend returns 403 (not 401) for bad JWT on many routes; sync UI session
    if (isAuthSessionRejected(status, data)) {
      clearStoredAuthSession({ notify: true });
    }
    return Promise.reject(error);
  }
);

const authService = {
  // Register new user
  register: async (email, password, confirmPassword) => {
    const response = await api.post('/auth/register', {
      email,
      password,
      confirmPassword
    });
    return response.data;
  },

  // Login user
  login: async (email, password, rememberMe = false) => {
    const response = await api.post('/auth/login', {
      email,
      password,
      rememberMe
    });
    return response.data;
  },

  // Logout user
  logout: async () => {
    const response = await api.post('/auth/logout');
    return response.data;
  },

  // Get user profile
  getProfile: async () => {
    const response = await api.get('/auth/profile');
    return response.data;
  },

  // Update user profile
  updateProfile: async (profileData) => {
    const response = await api.put('/auth/profile', profileData);
    return response.data;
  },

  // Verify email
  verifyEmail: async (email, code) => {
    const response = await api.post('/auth/verify-email', { email, code });
    return response.data;
  },

  // Resend verification email
  resendVerification: async (email) => {
    const response = await api.post('/auth/resend-verification', { email });
    return response.data;
  },

  // Validate token
  validateToken: async () => {
    const response = await api.get('/auth/validate');
    return response.data;
  },

  // Verify password for authenticated user
  verifyPassword: async (email, password) => {
    const response = await api.post('/auth/verify-password', { email, password });
    return response.data;
  }
};

export default authService;
